import { Response as ApplicationResponse } from '../../../api/typegen/client/application/get';
import { Response as ApplicationStatusResponse } from '../../../api/typegen/client/application/check-status/get';
import { datadogLogs } from '@datadog/browser-logs';
import { getFetchHeaders } from '../../../utils/fetchHeaders';
import { isNotProduction } from '../../../utils/isNotProduction';
import { LOGIN_ROUTE } from '../../../utils/constants';
import { routes } from '../../../routes';
import { useAnalytics } from 'use-analytics';
import { useEffect, useRef, useState } from 'react';
import {
  useGetClientApplication,
  useGetClientApplicationCheckStatus,
} from '../../../api';
import { useNavigate } from 'react-router-dom';
import { useNavigateOrigin } from '../../../hooks/useNavigateOrigin';
import { usePolling } from '../../../hooks/usePolling';
import useAuth from '../../../hooks/useAuth';
import useTink from '../../../hooks/useTink';

const POLLING_INTERVAL = 5000;
const MAX_ATTEMPTS = 3;

const LOGIN_ERROR_REDIRECTION_ROUTE = `${LOGIN_ROUTE}?errorMessage=validation.session`;

export default function useResolutionResolverOnlineBank() {
  const navigate = useNavigate();
  const { navigateOrigin } = useNavigateOrigin();
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const { logout } = useAuth();
  const { track } = useAnalytics();
  const { handleTinkV2Error } = useTink();
  const { fetch: fetchGetClientApplicationCheckStatus } =
    useGetClientApplicationCheckStatus();

  const attemptsApplication = useRef<number>(0);
  const attemptsStatus = useRef<number>(0);

  const [application, setApplication] = useState<
    ApplicationResponse | null | undefined
  >(null);
  const [applicationStatus, setApplicationStatus] =
    useState<ApplicationStatusResponse | null>(null);

  const fetchApplication = async () => {
    try {
      const applicationResponse = await fetchGetApplication({
        headers: getFetchHeaders({
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        }),
      });
      if (applicationResponse) setApplication(applicationResponse);
      else throw Error();
    } catch {
      if (attemptsApplication.current > MAX_ATTEMPTS) {
        applicationPolling.stopPolling();
        window.location.replace(LOGIN_ERROR_REDIRECTION_ROUTE);
      } else {
        attemptsApplication.current += 1;
      }
    }
  };

  const fetchApplicationStatus = async () => {
    try {
      const response = await fetchGetClientApplicationCheckStatus();
      if (response) setApplicationStatus(response);
      else throw Error();
    } catch {
      if (attemptsStatus.current > MAX_ATTEMPTS) {
        clientApplicationCheckStatusPolling.stopPolling();
        window.location.replace(LOGIN_ERROR_REDIRECTION_ROUTE);
      } else {
        attemptsStatus.current += 1;
      }
    }
  };

  const applicationPolling = usePolling(fetchApplication);
  const clientApplicationCheckStatusPolling = usePolling(
    fetchApplicationStatus,
  );

  /**
   * Effects
   */
  useEffect(() => {
    clientApplicationCheckStatusPolling.startPolling(POLLING_INTERVAL);

    return () => clientApplicationCheckStatusPolling.stopPolling();
  }, []);

  useEffect(() => {
    (function handleCanPollApplication() {
      if (applicationStatus?.completed !== true || applicationPolling.isEnabled)
        return;

      clientApplicationCheckStatusPolling.stopPolling();
      setTimeout(
        () => {
          applicationPolling.startPolling(POLLING_INTERVAL);
        },
        // Note: Hack to avoid Tink Extension's mock race condition
        isNotProduction() ? 15000 : 0,
      );
    })();
  }, [
    clientApplicationCheckStatusPolling.pollingAttempts,
    applicationStatus?.completed,
    applicationPolling.isEnabled,
  ]);

  useEffect(() => {
    (function handleApplicationResolution() {
      if (!application || !application?.resolution) return;

      applicationPolling.stopPolling();
      handleNavigateByApplicationResolution(application);
    })();
  }, [application]);

  useEffect(() => {
    handleTinkV2Error();
  }, []);

  /**
   * Functions
   */
  async function handleNavigateByApplicationResolution(
    application: ApplicationResponse,
  ) {
    const { resolution, resolutionDetail } = application;

    track('identify', {
      application,
    });

    // Before change this switch. Please, check this -> https://wiki.4finance.net/display/FD/Flow+outcome+mapping
    switch (resolution) {
      case 'APPROVED':
        return navigate(routes.FirstLoan_OnlineBank_Approved);

      case 'MANUAL':
        return navigate(routes.FirstLoan_OnlineBank_DocumentsRequired);

      case 'REJECTED':
        // To reproduce this error: tink extension -> APPROVED + APPROVED + 631
        if (resolutionDetail === 'NEW_OFFER') {
          return navigate(routes.best_offer);
        }
        if (
          resolutionDetail &&
          [
            'REJECTED_BY_THIRD_PARTY',
            'INSTANTOR_TOO_MANY_ACCOUNTS',
            'INSTANTOR_ID_MISMATCH',
          ].includes(resolutionDetail)
        ) {
          return navigate(routes.FirstLoan_OnlineBank_RejectedByTink);
        }
        if (
          resolutionDetail &&
          [
            'INVALID_PERSONAL_DATA',
            'LOW_IOVATION_SCORE',
            'RISKY_TELEPHONE',
          ].includes(resolutionDetail)
        ) {
          await logout();
          return navigateOrigin();
        }

        return navigate(routes.FirstLoan_Rejected);
      case 'CANCELLED':
        if (resolutionDetail === 'EXPIRED')
          return navigate(routes.FirstLoan_Welcome);
        return navigate(routes.FirstLoan_Rejected);
      default:
        // Note: Invalid state, user sees spinner indefinetly
        datadogLogs.logger.error('User ended up in invalid state.', {
          application,
        });
        return;
    }
  }
}
